import React from "react";
import vi from "./trans-vi";
import { useTranslation } from "react-i18next";
import { Button, Paragraph, useTheme } from "react-native-paper";
import { StyleSheet, useWindowDimensions } from "react-native";
import { Block } from "app/shared/components/blocks/block";
import { QueueSpacing } from "app/shared/components/spacing";
import { RegisterOptions, useForm } from "react-hook-form";
import { spacing } from "app/shared/styles/spacing";
import { registerTranslation } from "react-native-paper-dates";
import moment from "moment";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import { InputSelect } from "app/shared/components/form/input";
import { CARDITEM } from "app/shared/services/services-model.type";
import { RouteProp, useRoute } from "@react-navigation/core";
import StackParamsList from "app/navigation/nav-stack-params";
import ScreenNameType from "app/shared/constant/nav-screen.type";
import { InputSelectPicker } from "app/shared/components/form/input/input-select-picker";
import { CompareDateToDate, CompareDateToDateDistance } from "app/shared/util/common";
import { useDispatch, useSelector } from "react-redux";
import { getCardListSelector } from "../../reducer/selectors";
import { fetchCardListRequest } from "../../reducer/actions";
import CardSourceModal from "app/shared/components/modal/card-source/card-source.modal";
import { ArrayCreditShow, ArrayDebitShowHistory } from "../../card-utils/card-utils";

export type FormFilterCardHistory = {
  cardID: string;
  cardNum: string;
  fromDate: string;
  toDate: string;
};

interface FilterCardHistoryTableProps {
  onChangeSearch: (values: FormFilterCardHistory) => void;
  card?: CARDITEM;
  showModal: Function;
  closeModal: Function;
  onReset?: Function;
}

enum TYPE {
  OnChangeStartDate,
  OnChangeEndDate,
}

enum FormEnum {
  cardID,
  cardNum,
  fromDate,
  toDate,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

type ProfileScreenRouteProp = RouteProp<StackParamsList, ScreenNameType.CARD_HISTORY>;

const FilterCardHistoryTable = (props: FilterCardHistoryTableProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const { width } = useWindowDimensions();

  // ĐĂNG KÍ NGÔN NGỮ TIẾNG VIỆT
  registerTranslation("vi", vi);

  const { params } = useRoute<ProfileScreenRouteProp>();

  const listCard = useSelector(getCardListSelector);

  const { onChangeSearch = () => {}, showModal, closeModal, onReset = () => {} } = props;
  const [startDate, setStartDate] = React.useState<string | undefined>(moment(moment.now()).subtract(5, "day").format("DD/MM/YYYY"));
  const [endDate, setEndDate] = React.useState<string | undefined>(moment(moment.now()).format("DD/MM/YYYY"));
  const [cardSelect, setCardSelect] = React.useState<CARDITEM | undefined>(params?.card);

  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, clearErrors, trigger } = useForm<FormFilterCardHistory>({
    defaultValues: {
      cardID: "",
      cardNum: "",
      fromDate: "",
      toDate: "",
    },
    mode: "onChange",
  });

  const checkTime = (start?: string, end?: string) => {
    if (start && end) {
      if (!CompareDateToDate(start, end)) {
        return { error: true, message: t("ACC_THONG_BAO_NGAY_BAT_DAU_TRUOC_NGAY_KET_THUC") };
      } else if (!CompareDateToDateDistance(90, start, end)) {
        return { error: true, message: t("ACC_THONG_BAO_NGAY_BAT_DAU_KHONG_QUA_SO_NGAY").replace("{day}", "90") };
      }
    }
    return { error: false, message: "" };
  };

  const validateSchemeForm: ValidDataSchemeFormData = {
    cardID: {
      required: {
        value: false,
        message: "",
      },
    },
    cardNum: {
      required: {
        value: true,
        message: t("CARD_THONG_BAO_CHUA_CHON_THE"),
      },
    },
    fromDate: {
      required: {
        value: true,
        message: t("ACC_VUI_LONG_CHON_NGAY_BAT_DAU"),
      },
      validate: {
        check: (value: string) => {
          const { error, message } = checkTime(value, getValues("toDate"));
          if (error) return message;
          return true;
        },
      },
    },
    toDate: {
      required: {
        value: true,
        message: t("ACC_VUI_LONG_CHON_NGAY_KET_THUC"),
      },
      validate: {
        check: (value: string) => {
          const { error, message } = checkTime(getValues("fromDate"), value);
          if (error) return message;
          return true;
        },
      },
    },
  };

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        titleStyle: { ...fonts.medium, minWidth: 100 },
        textInput: {},
        buttonContainer: { alignSelf: "center" },
      }),
    [fonts],
  );

  const handleChange = (_type: TYPE) => (_date: string | undefined) => {
    clearErrors("fromDate");
    clearErrors("toDate");
    switch (_type) {
      case TYPE.OnChangeStartDate:
        if (_date !== startDate) onReset();
        setStartDate(_date);
        break;
      case TYPE.OnChangeEndDate:
        if (_date !== endDate) onReset();
        setEndDate(_date);
        break;
      default:
        break;
    }
  };

  const requestSearch = React.useCallback(
    (value: FormFilterCardHistory) => {
      onChangeSearch(value);
    },
    [onChangeSearch],
  );

  const onPressShowHistory = React.useCallback(() => {
    handleSubmit(requestSearch).call(() => {});
  }, [handleSubmit, requestSearch]);

  const handlerSelectCard = React.useCallback(
    (_card: CARDITEM) => {
      closeModal();
      if (_card.cardId?.length) {
        clearErrors("cardNum");
        setCardSelect(_card);
      }
    },
    [clearErrors, closeModal],
  );

  const onPressCard = () => {
    if (!listCard?.length) dispatch(fetchCardListRequest());
    showModal(
      <CardSourceModal
        onPressAcceptCard={handlerSelectCard}
        statusArrayCredit={ArrayCreditShow}
        statusArrayDebit={ArrayDebitShowHistory}
      />,
    );
  };

  const ButtonRow = (
    <Block flexwrap w={"100%"} row={isDesktop ? true : false} middle pv={spacing.huge}>
      <Button onPress={onPressShowHistory} uppercase={false} mode="contained" style={styles.buttonContainer}>
        {t("ACC_XEM_GIAO_DICH")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    if (startDate) {
      setValue("fromDate", startDate);
    }
    if (endDate) setValue("toDate", endDate);
  }, [endDate, setValue, startDate]);

  React.useEffect(() => {
    if (cardSelect) {
      if (cardSelect.cardId !== getValues("cardID")) onReset();
      setValue("cardID", cardSelect.cardId || "");
      setValue("cardNum", cardSelect.cardNum || "");
      trigger("cardNum");
    }
  }, [cardSelect, getValues, setValue, onReset, trigger]);

  React.useEffect(() => {
    if (!params?.card && !cardSelect) {
      if (listCard && listCard.length > 0) {
        setCardSelect(listCard[0]);
      }
    }
  }, [cardSelect, listCard, params?.card]);

  return (
    <>
      <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
        <Block row={isDesktop} center={isDesktop}>
          <Block mr={isDesktop ? spacing.huge : spacing.none}>
            <Paragraph style={styles.titleStyle}>{t("CARD_TAI_KHOAN_THE")}</Paragraph>
          </Block>
          <Block flex={1}>
            <InputSelect
              control={control}
              name="cardNum"
              label={t("CARD_TAI_KHOAN_THE")}
              rules={validateSchemeForm.cardNum}
              style={styles.textInput}
              onClick={onPressCard}
            />
          </Block>
        </Block>
        <Block row={isDesktop} center={isDesktop}>
          <Block mr={isDesktop ? spacing.huge : spacing.none}>
            <Paragraph style={styles.titleStyle}>{t("CARD_THOI_GIAN")}</Paragraph>
          </Block>
          <Block flex={1} row={isDesktop} spaced>
            <Block w={isDesktop ? "50%" : width - 2 * spacing.large - 2 * spacing.huge + 22}>
              <InputSelectPicker
                label={t("COMMON_TU_NGAY")}
                name={"fromDate"}
                control={control}
                style={styles.textInput}
                rules={validateSchemeForm.fromDate}
                onChangeDate={handleChange(TYPE.OnChangeStartDate)}
                inputProps={{ inputMode: "start" }}
                value={startDate}
              />
            </Block>
            <QueueSpacing size={"huge"} />
            <Block w={isDesktop ? "50%" : width - 2 * spacing.large - 2 * spacing.huge + 22}>
              <InputSelectPicker
                label={t("COMMON_DEN_NGAY")}
                name={"toDate"}
                control={control}
                style={styles.textInput}
                rules={validateSchemeForm.toDate}
                onChangeDate={handleChange(TYPE.OnChangeEndDate)}
                inputProps={{ inputMode: "end" }}
                value={endDate}
              />
            </Block>
          </Block>
        </Block>
      </Block>
      {ButtonRow}
    </>
  );
};

export default FilterCardHistoryTable;
